import api from './api';
import internationalTransferService from './internationalTransferService';

const transactionService = {
  getTransactions: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      
      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          params.append(key, value);
        }
      });
      
      // The API call automatically includes Authorization header via api.js
      const response = await api.get(`/banking/transactions/?${params}`);
      
      // Handle different response formats
      const data = response.data.results || response.data;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  },

  getRecentTransactions: async () => {
    try {
      const response = await api.get('/banking/transactions/recent/');
      const data = response.data.results || response.data;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error fetching recent transactions:', error);
      throw error;
    }
  },

  createTransfer: async (transferData) => {
    try {
      // First check if verification is required
      try {
        const verificationCheck = await internationalTransferService.checkVerificationRequired(
          'domestic', 
          transferData.amount
        );
        
        if (!verificationCheck.can_proceed) {
          throw {
            response: {
              data: {
                error: 'Verification required',
                message: verificationCheck.message,
                needs_verification: true,
                current_step: verificationCheck.current_step
              }
            }
          };
        }
      } catch (error) {
        // If verification check fails, still try to create transfer
        console.warn('Verification check failed, proceeding with transfer:', error.message);
      }
      
      // Proceed with transfer
      const response = await api.post('/banking/transactions/transfer/', transferData);
      return response.data;
    } catch (error) {
      console.error('Error creating transfer:', error);
      
      // Check if it's a verification error
      if (error.response?.data?.needs_verification) {
        throw {
          ...error.response.data,
          isVerificationError: true
        };
      }
      
      throw error;
    }
  },

  getBeneficiaries: async () => {
    try {
      const response = await api.get('/banking/beneficiaries/');
      const data = response.data.results || response.data;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error fetching beneficiaries:', error);
      throw error;
    }
  },

  addBeneficiary: async (beneficiaryData) => {
    try {
      const response = await api.post('/banking/beneficiaries/', beneficiaryData);
      return response.data;
    } catch (error) {
      console.error('Error adding beneficiary:', error);
      throw error;
    }
  },

  deleteBeneficiary: async (beneficiaryId) => {
    try {
      await api.delete(`/banking/beneficiaries/${beneficiaryId}/`);
    } catch (error) {
      console.error('Error deleting beneficiary:', error);
      throw error;
    }
  },

  // ============ VERIFICATION METHODS ============
  checkVerificationRequired: async (transferType, amount) => {
    try {
      return await internationalTransferService.checkVerificationRequired(transferType, amount);
    } catch (error) {
      console.error('Error checking verification:', error);
      throw error;
    }
  },

  getVerificationStatus: async () => {
    try {
      return await internationalTransferService.getVerificationStatus();
    } catch (error) {
      console.error('Error getting verification status:', error);
      throw error;
    }
  },

  verifyAccountStep: async (code) => {
    try {
      return await internationalTransferService.verifyAccountStep(code);
    } catch (error) {
      console.error('Error verifying account step:', error);
      throw error;
    }
  },

  // NEW: Get transaction by ID
  getTransactionById: async (transactionId) => {
    try {
      const response = await api.get(`/banking/transactions/${transactionId}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching transaction by ID:', error);
      throw error;
    }
  },

  // NEW: Get account-specific transactions
  getAccountTransactions: async (accountId) => {
    try {
      const response = await api.get(`/banking/transactions/?account_id=${accountId}`);
      const data = response.data.results || response.data;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error fetching account transactions:', error);
      throw error;
    }
  }
};

export default transactionService;